import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Icon from "../Shared/Icon";
import { KAVACH_COLORS } from "./KavachData";

const MIN_TRACK_WIDTH = 300;
const NARROW_TRACK_WIDTH = 560;
const DEFAULT_TRACK_WIDTH = 780;
const TRACK_HEIGHT = 1720;
const LEFT_FRAC = 190 / 780;
const RIGHT_FRAC = 590 / 780;
const CENTER_FRAC = 0.5;
const TOP_MARGIN = 110;
const BOTTOM_MARGIN = 110;
const TRACK_SIDE_PADDING = 32;

// On narrow tracks the desktop zig-zag pushes nodes (and their side labels)
// past the edge, so pull the two columns closer to the centre.
const getSideFracs = (trackWidth) =>
  trackWidth < NARROW_TRACK_WIDTH
    ? { left: 0.28, right: 0.72 }
    : { left: LEFT_FRAC, right: RIGHT_FRAC };

const getSideLabelWidth = (trackWidth) =>
  Math.max(110, Math.min(300, trackWidth * 0.42));

const getNodePositions = (trackWidth, milestones) => {
  const step =
    (TRACK_HEIGHT - TOP_MARGIN - BOTTOM_MARGIN) /
    Math.max(1, milestones.length - 1);
  const sideFracs = getSideFracs(trackWidth);
  return milestones.map((m, i) => {
    const y = TRACK_HEIGHT - BOTTOM_MARGIN - i * step;
    const frac =
      m.side === "left"
        ? sideFracs.left
        : m.side === "right"
          ? sideFracs.right
          : CENTER_FRAC;
    return { x: trackWidth * frac, y };
  });
};

const buildPathD = (points) => {
  let d = `M ${points[0].x} ${TRACK_HEIGHT} C ${points[0].x} ${TRACK_HEIGHT - 90}, ${points[0].x} ${
    points[0].y + 90
  }, ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const midY = (p0.y + p1.y) / 2;
    d += ` C ${p0.x} ${midY}, ${p1.x} ${midY}, ${p1.x} ${p1.y}`;
  }
  return d;
};

const formatPoints = (n) => n.toLocaleString("en-IN");

const KavachJourney = forwardRef(({
  milestones = [],
  localFrac = 0,
  overallFrac = 0,
  canClaim = false,
  onClaimClick,
}, ref) => {
  const scrollRef = useRef(null);
  const pathRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(DEFAULT_TRACK_WIDTH);
  const [dash, setDash] = useState({ len: 0, offset: 0 });
  const [ringOffset, setRingOffset] = useState(0);
  const hasScrolledRef = useRef(false);

  const points = useMemo(
    () => (milestones.length ? getNodePositions(trackWidth, milestones) : []),
    [trackWidth, milestones],
  );
  const pathD = useMemo(
    () => (points.length ? buildPathD(points) : ""),
    [points],
  );

  const ringRadius = 44;
  const ringCircumference = 2 * Math.PI * ringRadius;

  const sideLabelWidth = getSideLabelWidth(trackWidth);

  const currentIndex = useMemo(
    () => milestones.findIndex((m) => m.status === "current"),
    [milestones],
  );

  const scrollToNextLevel = () => {
    if (!scrollRef.current || currentIndex < 0) return;
    const nextPos = points[currentIndex + 1];
    if (!nextPos) return;
    const el = scrollRef.current;
    const targetScrollTop = nextPos.y - el.clientHeight / 2;
    el.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
  };

  useImperativeHandle(ref, () => ({ scrollToNextLevel }));

  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const updateWidth = () => {
      const available = el.clientWidth - TRACK_SIDE_PADDING;
      setTrackWidth(Math.max(MIN_TRACK_WIDTH, available));
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (pathRef.current && pathD) {
      const total = pathRef.current.getTotalLength();
      setDash({ len: total, offset: total * (1 - overallFrac) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathD]);

  useEffect(() => {
    if (scrollRef.current && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    const t = setTimeout(() => {
      setRingOffset(ringCircumference * (1 - localFrac));
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFrac]);

  return (
    <div style={styles.panel} className="kavach-pressable kavach-journey-panel">
      <div style={styles.panelHeader}>
        <div style={styles.panelTitleRow}>
          <Icon name="trophy" size={20} color={KAVACH_COLORS.gold} />
          <span style={styles.panelTitle}>Your Rewards Journey</span>
        </div>
        <span style={styles.panelSub}>
          Climb the path, unlock rewards as you shop
        </span>
      </div>

      <div
        ref={scrollRef}
        className="kavach-scroll kavach-journey-scroll"
        style={styles.scrollArea}>
        <div
          style={{
            position: "relative",
            width: trackWidth,
            height: TRACK_HEIGHT,
          }}>
          <svg
            width={trackWidth}
            height={TRACK_HEIGHT}
            viewBox={`0 0 ${trackWidth} ${TRACK_HEIGHT}`}
            style={{ position: "absolute", top: 0, left: 0 }}>
            <defs>
              <linearGradient id="kavach-road-grad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor={KAVACH_COLORS.purple} />
                <stop offset="100%" stopColor={KAVACH_COLORS.gold} />
              </linearGradient>
            </defs>

            <path
              d={pathD}
              fill="none"
              stroke="#DCE2EA"
              strokeWidth="16"
              strokeLinecap="round"
            />

            <path
              ref={pathRef}
              d={pathD}
              fill="none"
              stroke="url(#kavach-road-grad)"
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={dash.len}
              strokeDashoffset={dash.offset}
              style={{
                transition: "stroke-dashoffset 1.2s cubic-bezier(.22,1,.36,1)",
              }}
            />

            <path
              d={pathD}
              fill="none"
              stroke="#FFFFFF"
              strokeOpacity="0.85"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="2 16"
              className="kavach-march"
            />
          </svg>

          {milestones.map((m, i) => {
            const pos = points[i];
            const isCurrent = m.status === "current";
            const isClaimed = m.status === "claimed";
            const isLocked = m.status === "locked" && !m.grand;
            const nodeWidth = Math.min(190, trackWidth * 0.44);
            const labelSide =
              m.side === "left"
                ? "right"
                : m.side === "right"
                  ? "left"
                  : "bottom";
            const cardWidth = Math.min(210, trackWidth * 0.5);
            const grandWidth = Math.min(220, trackWidth * 0.56);

            if (m.grand) {
              return (
                <div
                  key={m.id}
                  style={{
                    ...styles.nodeWrap,
                    width: grandWidth,
                    left: pos.x,
                    top: pos.y,
                  }}>
                  <div
                    style={{ ...styles.grandCard, width: grandWidth }}
                    className="kavach-pressable">
                    <div style={styles.grandShimmerWrap}>
                      <div
                        className="kavach-shimmer"
                        style={styles.grandShimmer}
                      />
                    </div>
                    <Icon name={m.icon} size={30} color="#3A2A05" />
                    <div style={styles.grandTitle}>{m.title}</div>
                    <div style={styles.grandPoints}>
                      {formatPoints(m.points)} pts
                    </div>
                    <div style={styles.grandBadge}>
                      <Icon name="lock" size={12} color="#3A2A05" />
                      <span>Grand Prize</span>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={m.id}
                style={{
                  ...styles.nodeWrap,
                  width: nodeWidth,
                  left: pos.x,
                  top: pos.y,
                  opacity: isLocked ? 0.55 : 1,
                }}>
                {isCurrent && (
                  <div style={styles.youAreHerePill} className="kavach-float">
                    {canClaim ? "TAP TO CLAIM" : "YOU ARE HERE"}
                  </div>
                )}

                <div style={styles.avatarAnchor}>
                  <div
                    style={{
                      ...styles.avatarOuter,
                      ...(isCurrent ? {} : { width: 64, height: 64 }),
                      ...(isCurrent && canClaim ? { cursor: "pointer" } : {}),
                    }}
                    className={isCurrent ? "kavach-halo" : ""}
                    onClick={isCurrent && canClaim ? onClaimClick : undefined}
                    role={isCurrent && canClaim ? "button" : undefined}>
                    {isCurrent && (
                      <svg
                        width={88}
                        height={88}
                        style={styles.ringSvg}
                        viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r={ringRadius}
                          fill="none"
                          stroke="#1B2A4D"
                          strokeWidth="6"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r={ringRadius}
                          fill="none"
                          stroke={KAVACH_COLORS.gold}
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={ringCircumference}
                          strokeDashoffset={ringOffset}
                          transform="rotate(-90 50 50)"
                          style={{
                            transition:
                              "stroke-dashoffset 1.1s cubic-bezier(.22,1,.36,1)",
                          }}
                        />
                      </svg>
                    )}
                    <div
                      style={{
                        ...styles.avatarCircle,
                        background: isCurrent
                          ? `linear-gradient(150deg, ${KAVACH_COLORS.purple}, ${KAVACH_COLORS.indigo})`
                          : isClaimed
                            ? `linear-gradient(150deg, ${KAVACH_COLORS.green}, #1F8A54)`
                            : "linear-gradient(150deg, #C7CDD6, #9BA6B3)",
                        width: isCurrent ? 88 : 64,
                        height: isCurrent ? 88 : 64,
                      }}>
                      <Icon
                        name={m.icon}
                        size={isCurrent ? 34 : 26}
                        color="#fff"
                      />
                      {isClaimed && (
                        <div style={styles.checkBadge}>
                          <Icon
                            name="check"
                            size={12}
                            color="#fff"
                            strokeWidth={3}
                          />
                        </div>
                      )}
                      {isLocked && (
                        <div style={styles.lockBadge}>
                          <Icon name="lock" size={12} color="#fff" />
                        </div>
                      )}
                    </div>
                  </div>

                  {labelSide !== "bottom" && (
                    <div
                      style={{
                        ...styles.sideLabel,
                        width: sideLabelWidth,
                        ...(labelSide === "right"
                          ? { left: "100%", marginLeft: 14, textAlign: "left" }
                          : {
                              right: "100%",
                              marginRight: 14,
                              textAlign: "right",
                            }),
                      }}>
                      <div
                        style={{
                          ...styles.nodeLabel,
                          marginTop: 0,
                          textAlign: "inherit",
                        }}>
                        <span style={styles.nodeStep}>#{m.step}</span> {m.title}
                      </div>
                      <div
                        style={{ ...styles.nodePoints, textAlign: "inherit" }}>
                        {formatPoints(m.points)} pts
                      </div>
                    </div>
                  )}
                </div>

                {labelSide === "bottom" && (
                  <>
                    <div style={styles.nodeLabel}>
                      <span style={styles.nodeStep}>#{m.step}</span> {m.title}
                    </div>
                    <div style={styles.nodePoints}>
                      {formatPoints(m.points)} pts
                    </div>
                  </>
                )}

                {isCurrent && (
                  <div
                    style={{ ...styles.currentCard, width: cardWidth }}
                    className="kavach-fade-up">
                    {canClaim ? (
                      <button
                        className="kavach-pressable"
                        style={styles.currentClaimBtn}
                        onClick={onClaimClick}>
                        <Icon name="gift" size={14} color="#1E1B2E" />
                        Claim Your Reward
                      </button>
                    ) : (
                      <>
                        <div style={styles.currentCardTop}>
                          <span>{formatPoints(m.remaining)} pts to go</span>
                          <span style={{ color: KAVACH_COLORS.gold }}>
                            {Math.round(localFrac * 100)}%
                          </span>
                        </div>
                        <div style={styles.currentBarTrack}>
                          <div
                            style={{
                              ...styles.currentBarFill,
                              width: `${Math.round(localFrac * 100)}%`,
                            }}>
                            <div
                              className="kavach-shimmer"
                              style={{ position: "absolute", inset: 0 }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

const styles = {
  panel: {
    background: "#FFFFFF",
    borderRadius: 20,
    boxShadow: "0 8px 28px rgba(75,68,149,0.08)",
    height: 1010,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
    minWidth: 0,
  },
  panelHeader: {
    padding: "20px 24px 14px",
    borderBottom: "1px solid #E9ECF2",
  },
  panelTitleRow: { display: "flex", alignItems: "center", gap: 8 },
  panelTitle: { fontSize: 16, fontWeight: 600, color: KAVACH_COLORS.ink },
  panelSub: {
    fontSize: 12.5,
    color: KAVACH_COLORS.slate,
    marginTop: 4,
    display: "block",
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "center",
    padding: "36px 16px",
  },
  nodeWrap: {
    position: "absolute",
    transform: "translate(-50%, -50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  avatarOuter: {
    position: "relative",
    width: 88,
    height: 88,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
  },
  ringSvg: { position: "absolute", top: 0, left: 0 },
  avatarCircle: {
    position: "relative",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(75,68,149,0.28)",
  },
  checkBadge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: KAVACH_COLORS.green,
    border: "2px solid #fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lockBadge: {
    position: "absolute",
    bottom: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: KAVACH_COLORS.slate,
    border: "2px solid #fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarAnchor: {
    position: "relative",
  },
  sideLabel: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 300,
  },
  nodeLabel: {
    marginTop: 10,
    fontSize: 13,
    fontWeight: 600,
    color: KAVACH_COLORS.ink,
    textAlign: "center",
  },
  nodeStep: { color: "#8C9BB5", fontWeight: 700 },
  nodePoints: {
    fontSize: 12,
    color: KAVACH_COLORS.slate,
    marginTop: 2,
    fontWeight: 500,
  },
  youAreHerePill: {
    position: "absolute",
    top: -46,
    background: KAVACH_COLORS.ink,
    color: KAVACH_COLORS.gold,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    padding: "6px 12px",
    borderRadius: 20,
    whiteSpace: "nowrap",
    boxShadow: "0 6px 14px rgba(0,0,0,0.25)",
  },
  currentCard: {
    marginTop: 12,
    background: `linear-gradient(150deg, ${KAVACH_COLORS.purpleDark} 0%, #16283A 100%)`,
    borderRadius: 14,
    padding: "12px 14px",
    width: 210,
    boxShadow: "0 10px 24px rgba(75,68,149,0.3)",
  },
  currentClaimBtn: {
    width: "100%",
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#1E1B2E",
    background: `linear-gradient(150deg, ${KAVACH_COLORS.gold}, ${KAVACH_COLORS.goldDark})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    cursor: "pointer",
  },
  currentCardTop: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11.5,
    color: "#E4DEF7",
    fontWeight: 600,
    marginBottom: 8,
  },
  currentBarTrack: {
    height: 8,
    borderRadius: 8,
    background: "rgba(255,255,255,0.12)",
    overflow: "hidden",
  },
  currentBarFill: {
    position: "relative",
    height: "100%",
    borderRadius: 8,
    background: `linear-gradient(90deg, ${KAVACH_COLORS.gold}, #FFE9A0)`,
    overflow: "hidden",
  },
  grandCard: {
    position: "relative",
    width: 220,
    padding: "18px 16px",
    borderRadius: 18,
    background: `linear-gradient(155deg, ${KAVACH_COLORS.gold} 0%, ${KAVACH_COLORS.goldDark} 100%)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 12px 30px rgba(255,165,0,0.4)",
    overflow: "hidden",
  },
  grandShimmerWrap: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  grandShimmer: { position: "absolute", inset: 0 },
  grandTitle: { fontSize: 14.5, fontWeight: 700, color: "#3A2A05" },
  grandPoints: { fontSize: 12.5, color: "#4E3A0E", fontWeight: 600 },
  grandBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(58,42,5,0.14)",
    color: "#3A2A05",
    fontSize: 10.5,
    fontWeight: 700,
    padding: "4px 10px",
    borderRadius: 20,
    marginTop: 4,
  },
};

export default KavachJourney;
